import * as THREE from 'three';
import type {
    VisibleModelEdgeProjectionBuildInput,
    VisibleModelEdgeProjectedSegment,
    VisibleModelEdgeProjectionRecord,
    VisibleModelEdgeReferencePoint,
    ProjectedModelConicSnapPointResolverPort,
} from '../../../types/sketch/VisibleModelEdgeProjection';
import { ProjectedModelConicSnapPointResolver } from './ProjectedModelConicSnapPointResolver';

/** 可见性分段递归的最低层级，避免仅检查端点和中点而漏掉窄可见区间。 */
const VISIBILITY_MIN_SUBDIVISION_DEPTH = 2;

/** 可见性分段递归的最高层级。 */
const VISIBILITY_MAX_SUBDIVISION_DEPTH = 6;

/** 相邻可见参数区间合并容差。 */
const PARAMETER_MERGE_EPSILON = 1e-6;

/** 投影后退化线段判定容差平方。 */
const PROJECTED_SEGMENT_EPSILON_SQUARED = 1e-18;

/**
 * 可见模型边正交投影服务。
 *
 * 职责：
 * 1. 从权威拓扑快照读取真实模型边及曲线采样；
 * 2. 通过注入的遮挡查询把源边裁剪为可见参数区间；
 * 3. 将可见子段正交投影到指定草图平面；
 * 4. 输出自身可见的真实端点、中点、投影中心与有效象限点。
 *
 * 本服务不创建 THREE 场景对象、不处理鼠标拾取，也不决定捕捉开关。
 */
export class VisibleModelEdgeProjectionService {
    /** 复用的局部点转世界点缓存。 */
    private readonly worldPointBuffer = new THREE.Vector3();

    /** 投影圆/椭圆中心与象限点解析端口。 */
    private readonly conicSnapPointResolver: ProjectedModelConicSnapPointResolverPort;

    /**
     * 创建可见模型边正交投影服务。
     *
     * @param conicSnapPointResolver 投影圆锥曲线参考点解析端口。
     */
    public constructor(
        conicSnapPointResolver: ProjectedModelConicSnapPointResolverPort
            = new ProjectedModelConicSnapPointResolver(),
    ) {
        this.conicSnapPointResolver = conicSnapPointResolver;
    }

    /**
     * 构建当前草图平面的可见模型边投影记录。
     *
     * @param input 权威结果体、参考面与遮挡查询。
     * @returns 仍包含至少一个可见投影子段的拓扑边记录。
     */
    public build(input: VisibleModelEdgeProjectionBuildInput): VisibleModelEdgeProjectionRecord[] {
        const records: VisibleModelEdgeProjectionRecord[] = [];

        input.resultMeshes.forEach((resultMesh) => {
            const topology = resultMesh.getTopologySnapshot();
            if (!topology) {
                return;
            }

            // 模型边采样点属于结果体局部坐标，统一在读取快照后应用结果体世界矩阵。
            resultMesh.updateWorldMatrix(true, false);
            const vertexMap = new Map(topology.vertices.map((vertex) => [vertex.index, vertex]));

            topology.edges.forEach((edge) => {
                const sourcePoints = this.resolveSourceWorldPoints(
                    edge,
                    vertexMap,
                    resultMesh.matrixWorld,
                );
                if (sourcePoints.length < 2) {
                    return;
                }

                const cumulativeLengths = this.computeCumulativeLengths(sourcePoints);
                const totalLength = cumulativeLengths[cumulativeLengths.length - 1] ?? 0;
                if (totalLength <= 1e-12) {
                    return;
                }

                const visibleSegments = this.resolveVisibleProjectedSegments(
                    sourcePoints,
                    cumulativeLengths,
                    totalLength,
                    input,
                    resultMesh,
                );
                if (visibleSegments.length === 0) {
                    return;
                }

                records.push({
                    sourceMesh: resultMesh,
                    edgeIndex: edge.index,
                    subEntityId: edge.subEntityId,
                    persistentRef: edge.ref,
                    curveKind: edge.curveKind,
                    visibleSegments,
                    referencePoints: this.resolveVisibleReferencePoints(
                        edge,
                        vertexMap,
                        sourcePoints,
                        cumulativeLengths,
                        totalLength,
                        visibleSegments,
                        input,
                        resultMesh,
                    ),
                });
            });
        });

        return records;
    }

    /**
     * 解析权威拓扑边的世界空间折线采样。
     *
     * @param edge 权威拓扑边快照。
     * @param vertexMap 权威顶点索引。
     * @param matrixWorld 结果体世界矩阵。
     * @returns 去除连续重复点后的世界空间采样点。
     */
    private resolveSourceWorldPoints(
        edge: TopologySnapshotEdge,
        vertexMap: ReadonlyMap<number, TopologySnapshotVertex>,
        matrixWorld: THREE.Matrix4,
    ): THREE.Vector3[] {
        const sampledPoints = edge.sampledPoints && edge.sampledPoints.length >= 2
            ? edge.sampledPoints
            : [
                vertexMap.get(edge.vertexAIndex)?.position,
                vertexMap.get(edge.vertexBIndex)?.position,
            ].filter((point): point is TopologyPoint3D => Boolean(point));
        const worldPoints: THREE.Vector3[] = [];

        sampledPoints.forEach((point) => {
            const worldPoint = this.worldPointBuffer
                .set(point[0], point[1], point[2])
                .applyMatrix4(matrixWorld);
            const previous = worldPoints[worldPoints.length - 1] ?? null;
            if (!previous || previous.distanceToSquared(worldPoint) > 1e-18) {
                worldPoints.push(worldPoint.clone());
            }
        });

        return worldPoints;
    }

    /**
     * 计算折线采样点的累计弧长。
     *
     * @param points 世界空间折线采样点。
     * @returns 与采样点等长的累计弧长数组。
     */
    private computeCumulativeLengths(points: readonly THREE.Vector3[]): number[] {
        const cumulativeLengths = [0];
        for (let index = 1; index < points.length; index += 1) {
            const previousLength = cumulativeLengths[index - 1] ?? 0;
            cumulativeLengths.push(previousLength + points[index - 1]!.distanceTo(points[index]!));
        }
        return cumulativeLengths;
    }

    /**
     * 解析并投影源边的全部可见子线段。
     *
     * @param sourcePoints 源边世界空间采样点。
     * @param cumulativeLengths 累计弧长。
     * @param totalLength 源边总弧长。
     * @param input 投影构建输入。
     * @param resultMesh 源边所属结果体。
     * @returns 已投影到草图平面的可见子线段。
     */
    private resolveVisibleProjectedSegments(
        sourcePoints: readonly THREE.Vector3[],
        cumulativeLengths: readonly number[],
        totalLength: number,
        input: VisibleModelEdgeProjectionBuildInput,
        resultMesh: VisibleModelEdgeProjectionRecord['sourceMesh'],
    ): VisibleModelEdgeProjectedSegment[] {
        const visibleIntervals: Array<[number, number]> = [];
        const visibilityByParameter = new Map<number, boolean>();

        for (let index = 0; index < sourcePoints.length - 1; index += 1) {
            const startParameter = (cumulativeLengths[index] ?? 0) / totalLength;
            const endParameter = (cumulativeLengths[index + 1] ?? totalLength) / totalLength;
            this.collectVisibleIntervals(
                sourcePoints[index]!,
                sourcePoints[index + 1]!,
                startParameter,
                endParameter,
                0,
                input,
                resultMesh,
                visibilityByParameter,
                visibleIntervals,
            );
        }

        const mergedIntervals = this.mergeVisibleIntervals(visibleIntervals);
        const projectedSegments: VisibleModelEdgeProjectedSegment[] = [];
        let visibleIntervalIndex = 0;
        for (let index = 0; index < sourcePoints.length - 1; index += 1) {
            const segmentStartParameter = (cumulativeLengths[index] ?? 0) / totalLength;
            const segmentEndParameter = (cumulativeLengths[index + 1] ?? totalLength) / totalLength;

            // 可见区间和采样段都已按参数升序，用双指针前进，避免曲线采样较密时出现平方级区间扫描。
            while (
                visibleIntervalIndex < mergedIntervals.length
                && mergedIntervals[visibleIntervalIndex]![1]
                    <= segmentStartParameter + PARAMETER_MERGE_EPSILON
            ) {
                visibleIntervalIndex += 1;
            }
            for (
                let intervalIndex = visibleIntervalIndex;
                intervalIndex < mergedIntervals.length;
                intervalIndex += 1
            ) {
                const [visibleStartParameter, visibleEndParameter] = mergedIntervals[intervalIndex]!;
                if (visibleStartParameter >= segmentEndParameter - PARAMETER_MERGE_EPSILON) {
                    break;
                }
                const overlapStartParameter = Math.max(
                    segmentStartParameter,
                    visibleStartParameter,
                );
                const overlapEndParameter = Math.min(
                    segmentEndParameter,
                    visibleEndParameter,
                );
                if (overlapEndParameter <= overlapStartParameter + PARAMETER_MERGE_EPSILON) {
                    continue;
                }

                // 可见区间只负责裁剪，投影几何仍逐段保留权威采样折线，避免圆形闭合为点或曲线被替换为弦。
                this.appendProjectedSourceSegment(
                    projectedSegments,
                    sourcePoints[index]!,
                    sourcePoints[index + 1]!,
                    segmentStartParameter,
                    segmentEndParameter,
                    overlapStartParameter,
                    overlapEndParameter,
                    input,
                );
            }
        }
        return projectedSegments;
    }

    /**
     * 裁剪并投影单个源采样折线段。
     *
     * @param output 可见投影线段输出数组。
     * @param sourceStart 源采样段起点。
     * @param sourceEnd 源采样段终点。
     * @param segmentStartParameter 源采样段起始归一化参数。
     * @param segmentEndParameter 源采样段终止归一化参数。
     * @param clippedStartParameter 裁剪后起始归一化参数。
     * @param clippedEndParameter 裁剪后终止归一化参数。
     * @param input 投影构建输入。
     */
    private appendProjectedSourceSegment(
        output: VisibleModelEdgeProjectedSegment[],
        sourceStart: THREE.Vector3,
        sourceEnd: THREE.Vector3,
        segmentStartParameter: number,
        segmentEndParameter: number,
        clippedStartParameter: number,
        clippedEndParameter: number,
        input: VisibleModelEdgeProjectionBuildInput,
    ): void {
        const parameterSpan = segmentEndParameter - segmentStartParameter;
        if (parameterSpan <= PARAMETER_MERGE_EPSILON) {
            return;
        }
        const localStartParameter = THREE.MathUtils.clamp(
            (clippedStartParameter - segmentStartParameter) / parameterSpan,
            0,
            1,
        );
        const localEndParameter = THREE.MathUtils.clamp(
            (clippedEndParameter - segmentStartParameter) / parameterSpan,
            0,
            1,
        );
        const clippedSourceStart = sourceStart.clone().lerp(sourceEnd, localStartParameter);
        const clippedSourceEnd = sourceStart.clone().lerp(sourceEnd, localEndParameter);
        const projectedStart = input.referencePlane.projectWorldPointToPlane(clippedSourceStart);
        const projectedEnd = input.referencePlane.projectWorldPointToPlane(clippedSourceEnd);

        // 正视投影后退化为点的采样段不能作为线索引候选，避免产生不可预期的拾取半径。
        if (projectedStart.distanceToSquared(projectedEnd) <= PROJECTED_SEGMENT_EPSILON_SQUARED) {
            return;
        }
        output.push({
            // 保存已裁剪的源坐标，使视觉反馈与捕捉共享可见段，避免重新拾取或高亮到投影平面。
            sourceStart: clippedSourceStart,
            sourceEnd: clippedSourceEnd,
            start: projectedStart,
            end: projectedEnd,
            sourceStartParameter: clippedStartParameter,
            sourceEndParameter: clippedEndParameter,
        });
    }

    /**
     * 递归收集单个源折线段的可见参数区间。
     *
     * 最低两层强制细分用于发现“端点与中点同为遮挡，但内部仍有窄可见段”的情况；达到最低层级后，
     * 仅在采样可见性不一致时继续细分，最终按子区间中点可见性决定是否保留。
     *
     * @param start 源子段起点。
     * @param end 源子段终点。
     * @param startParameter 源边归一化起始参数。
     * @param endParameter 源边归一化结束参数。
     * @param depth 当前递归层级。
     * @param input 投影构建输入。
     * @param resultMesh 源边所属结果体。
     * @param visibilityByParameter 当前源边已经完成的可见性查询缓存。
     * @param output 可见参数区间输出数组。
     */
    private collectVisibleIntervals(
        start: THREE.Vector3,
        end: THREE.Vector3,
        startParameter: number,
        endParameter: number,
        depth: number,
        input: VisibleModelEdgeProjectionBuildInput,
        resultMesh: VisibleModelEdgeProjectionRecord['sourceMesh'],
        visibilityByParameter: Map<number, boolean>,
        output: Array<[number, number]>,
    ): void {
        const midpoint = start.clone().lerp(end, 0.5);
        const midpointParameter = (startParameter + endParameter) * 0.5;
        const startVisible = this.resolvePointVisibility(
            startParameter,
            start,
            input,
            resultMesh,
            visibilityByParameter,
        );
        const midpointVisible = this.resolvePointVisibility(
            midpointParameter,
            midpoint,
            input,
            resultMesh,
            visibilityByParameter,
        );
        const endVisible = this.resolvePointVisibility(
            endParameter,
            end,
            input,
            resultMesh,
            visibilityByParameter,
        );
        const hasMixedVisibility = startVisible !== midpointVisible || midpointVisible !== endVisible;
        const mustSubdivide = depth < VISIBILITY_MIN_SUBDIVISION_DEPTH
            || (hasMixedVisibility && depth < VISIBILITY_MAX_SUBDIVISION_DEPTH);

        if (mustSubdivide) {
            this.collectVisibleIntervals(
                start,
                midpoint,
                startParameter,
                midpointParameter,
                depth + 1,
                input,
                resultMesh,
                visibilityByParameter,
                output,
            );
            this.collectVisibleIntervals(
                midpoint,
                end,
                midpointParameter,
                endParameter,
                depth + 1,
                input,
                resultMesh,
                visibilityByParameter,
                output,
            );
            return;
        }

        // 以子区间中点作为区间可见性代表，裁剪边界只保留参数，不会生成任何“伪拓扑端点”。
        if (midpointVisible) {
            output.push([startParameter, endParameter]);
        }
    }

    /**
     * 读取或计算源边指定参数点的遮挡可见性。
     *
     * 同一递归层级会反复访问共享端点；按参数缓存后，每个实际采样参数只触发一次 MeshBVH 射线，
     * 避免模型边数量较大时把重复查询放大到 pointer 会话初始化阶段。
     *
     * @param parameter 源边归一化参数。
     * @param point 参数对应的世界坐标。
     * @param input 投影构建输入。
     * @param resultMesh 源边所属结果体。
     * @param visibilityByParameter 当前源边可见性缓存。
     * @returns 当前参数点是否可见。
     */
    private resolvePointVisibility(
        parameter: number,
        point: THREE.Vector3,
        input: VisibleModelEdgeProjectionBuildInput,
        resultMesh: VisibleModelEdgeProjectionRecord['sourceMesh'],
        visibilityByParameter: Map<number, boolean>,
    ): boolean {
        const cached = visibilityByParameter.get(parameter);
        if (typeof cached === 'boolean') {
            return cached;
        }
        const visible = input.occlusionQuery.isPointVisible(point, resultMesh);
        visibilityByParameter.set(parameter, visible);
        return visible;
    }

    /**
     * 合并连续或重叠的可见参数区间。
     *
     * @param intervals 原始可见参数区间。
     * @returns 按参数升序排列的合并结果。
     */
    private mergeVisibleIntervals(intervals: readonly [number, number][]): Array<[number, number]> {
        if (intervals.length === 0) {
            return [];
        }
        const sorted = intervals
            .map(([start, end]) => [start, end] as [number, number])
            .sort((left, right) => left[0] - right[0]);
        const merged: Array<[number, number]> = [sorted[0]!];

        for (let index = 1; index < sorted.length; index += 1) {
            const current = sorted[index]!;
            const previous = merged[merged.length - 1]!;
            if (current[0] <= previous[1] + PARAMETER_MERGE_EPSILON) {
                previous[1] = Math.max(previous[1], current[1]);
            } else {
                merged.push(current);
            }
        }
        return merged;
    }

    /**
     * 解析自身可见的模型边捕捉参考点。
     *
     * 端点只认 `topological` 顶点；闭合圆/椭圆为了稳定身份生成的 `curve-reference`
     * 不得伪装成端点。圆锥曲线中心和象限点由投影后的采样曲线解析，并继续通过源参数
     * 与可见区间过滤。
     *
     * @param edge 权威拓扑边快照。
     * @param vertexMap 权威顶点索引。
     * @param sourcePoints 源边世界空间采样点。
     * @param cumulativeLengths 累计弧长。
     * @param totalLength 源边总弧长。
     * @param visibleSegments 可见投影子线段。
     * @param input 投影构建输入。
     * @param resultMesh 源边所属结果体。
     * @returns 可动态显示和捕捉的参考点。
     */
    private resolveVisibleReferencePoints(
        edge: TopologySnapshotEdge,
        vertexMap: ReadonlyMap<number, TopologySnapshotVertex>,
        sourcePoints: readonly THREE.Vector3[],
        cumulativeLengths: readonly number[],
        totalLength: number,
        visibleSegments: readonly VisibleModelEdgeProjectedSegment[],
        input: VisibleModelEdgeProjectionBuildInput,
        resultMesh: VisibleModelEdgeProjectionRecord['sourceMesh'],
    ): VisibleModelEdgeReferencePoint[] {
        const referencePoints: VisibleModelEdgeReferencePoint[] = [];
        const vertexA = vertexMap.get(edge.vertexAIndex) ?? null;
        const vertexB = vertexMap.get(edge.vertexBIndex) ?? null;
        const hasRealStartPoint = vertexA?.role === 'topological';
        const hasRealEndPoint = vertexB?.role === 'topological';
        const hasRealEndpoints = hasRealStartPoint && hasRealEndPoint;

        if (hasRealStartPoint) {
            this.appendVisibleEdgeReferencePoint(
                referencePoints,
                'endpoint',
                0,
                sourcePoints,
                cumulativeLengths,
                totalLength,
                visibleSegments,
                input,
                resultMesh,
            );
        }
        if (hasRealEndpoints) {
            this.appendVisibleEdgeReferencePoint(
                referencePoints,
                'midpoint',
                0.5,
                sourcePoints,
                cumulativeLengths,
                totalLength,
                visibleSegments,
                input,
                resultMesh,
            );
        }
        if (hasRealEndPoint) {
            this.appendVisibleEdgeReferencePoint(
                referencePoints,
                'endpoint',
                1,
                sourcePoints,
                cumulativeLengths,
                totalLength,
                visibleSegments,
                input,
                resultMesh,
            );
        }

        const projectedSamplePoints = sourcePoints.map((point) => (
            input.referencePlane.projectWorldPointToPlane(point)
        ));
        const sourceParameters = cumulativeLengths.map((length) => length / totalLength);
        const conicCandidates = this.conicSnapPointResolver.resolve({
            curveKind: edge.curveKind,
            sourceSamplePoints: sourcePoints,
            projectedSamplePoints,
            sourceParameters,
            hasRealEndpoints,
            referencePlane: input.referencePlane,
        });
        conicCandidates.forEach((candidate) => {
            if (candidate.kind === 'center') {
                referencePoints.push({
                    kind: candidate.kind,
                    sourcePosition: null,
                    projectedPosition: candidate.projectedPosition,
                    sourceParameter: null,
                });
                return;
            }
            // `kind` 是圆锥候选的可判别字段，此分支中源参数已稳定收窄为 number。
            this.appendVisibleEdgeReferencePoint(
                referencePoints,
                candidate.kind,
                candidate.sourceParameter,
                sourcePoints,
                cumulativeLengths,
                totalLength,
                visibleSegments,
                input,
                resultMesh,
                candidate.projectedPosition,
            );
        });
        return referencePoints;
    }

    /**
     * 按源边参数追加一个自身可见的边上参考点。
     *
     * @param output 参考点输出数组。
     * @param kind 端点、中点或象限点语义。
     * @param sourceParameter 源边归一化弧长参数。
     * @param sourcePoints 源边世界采样点。
     * @param cumulativeLengths 源边累计弧长。
     * @param totalLength 源边总弧长。
     * @param visibleSegments 可见投影子段。
     * @param input 投影构建输入。
     * @param resultMesh 源边所属结果体。
     * @param projectedPosition 已解析的投影位置；缺省时直接投影源点。
     */
    private appendVisibleEdgeReferencePoint(
        output: VisibleModelEdgeReferencePoint[],
        kind: 'endpoint' | 'midpoint' | 'quadrant',
        sourceParameter: number,
        sourcePoints: readonly THREE.Vector3[],
        cumulativeLengths: readonly number[],
        totalLength: number,
        visibleSegments: readonly VisibleModelEdgeProjectedSegment[],
        input: VisibleModelEdgeProjectionBuildInput,
        resultMesh: VisibleModelEdgeProjectionRecord['sourceMesh'],
        projectedPosition?: THREE.Vector3,
    ): void {
        const liesOnVisibleSegment = visibleSegments.some((segment) => (
            sourceParameter >= segment.sourceStartParameter - PARAMETER_MERGE_EPSILON
            && sourceParameter <= segment.sourceEndParameter + PARAMETER_MERGE_EPSILON
        ));
        if (!liesOnVisibleSegment) {
            return;
        }
        const sourcePosition = this.samplePolylineAtParameter(
            sourcePoints,
            cumulativeLengths,
            totalLength,
            sourceParameter,
        );
        if (!input.occlusionQuery.isPointVisible(sourcePosition, resultMesh)) {
            return;
        }
        output.push({
            kind,
            sourcePosition,
            projectedPosition: projectedPosition?.clone()
                ?? input.referencePlane.projectWorldPointToPlane(sourcePosition),
            sourceParameter,
        });
    }

    /**
     * 按归一化弧长参数在线性采样折线上求点。
     *
     * @param points 世界空间折线采样点。
     * @param cumulativeLengths 累计弧长。
     * @param totalLength 折线总弧长。
     * @param parameter 归一化弧长参数。
     * @returns 源边上的插值世界点。
     */
    private samplePolylineAtParameter(
        points: readonly THREE.Vector3[],
        cumulativeLengths: readonly number[],
        totalLength: number,
        parameter: number,
    ): THREE.Vector3 {
        const clampedParameter = THREE.MathUtils.clamp(parameter, 0, 1);
        const targetLength = totalLength * clampedParameter;

        for (let index = 0; index < points.length - 1; index += 1) {
            const segmentStartLength = cumulativeLengths[index] ?? 0;
            const segmentEndLength = cumulativeLengths[index + 1] ?? totalLength;
            if (targetLength > segmentEndLength && index < points.length - 2) {
                continue;
            }
            const segmentLength = segmentEndLength - segmentStartLength;
            const localParameter = segmentLength <= 1e-12
                ? 0
                : (targetLength - segmentStartLength) / segmentLength;
            return points[index]!.clone().lerp(points[index + 1]!, THREE.MathUtils.clamp(localParameter, 0, 1));
        }

        return points[points.length - 1]!.clone();
    }
}
